import React, { useContext, useEffect, useMemo, useState } from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import API from "../../api/api";
import { AuthContext } from "../../context/AuthContext";
import { errorAlert, successAlert } from "../../utils/alert";
import Swal from "sweetalert2";

const AUDIENCES = [
  { key: "players", label: "Players" },
  { key: "escorts", label: "Escorts" },
  { key: "volunteers", label: "Volunteers" },
  { key: "districts", label: "Districts" },
  { key: "excellence_awards", label: "Excellence Awards" },
];

const defaultSettings = {
  is_enabled: true,
  mailer: "smtp",
  smtp_host: "",
  smtp_port: 587,
  smtp_username: "",
  smtp_password: "",
  has_smtp_password: false,
  smtp_encryption: "tls",
  from_address: "",
  from_name: "",
  available_from_addresses: "",
  reply_to_address: "",
  reply_to_name: "",
  admin_notification_emails: "",
};

const emptyTemplateState = {
  key: "",
  name: "",
  subject: "",
  body: "",
  is_enabled: true,
  available_variables: [],
};

const MailCenter = () => {
  const { role } = useContext(AuthContext);
  const normalizedRole = (role || "").toLowerCase();
  const [activeTab, setActiveTab] = useState("send");

  const [settings, setSettings] = useState(defaultSettings);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState("");
  const [templateForm, setTemplateForm] = useState(emptyTemplateState);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateResetting, setTemplateResetting] = useState(false);

  const [audience, setAudience] = useState("players");
  const [playerProfileStatus, setPlayerProfileStatus] = useState("all");
  const [playerEventId, setPlayerEventId] = useState("");
  const [events, setEvents] = useState([]);
  const [awardStatusFilter, setAwardStatusFilter] = useState("all");
  const [awardYearFilter, setAwardYearFilter] = useState("");
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [search, setSearch] = useState("");

  const [compose, setCompose] = useState({
    send_mode: "selected",
    from_address: "",
    from_name: "",
    additional_emails: "",
    subject: "",
    body: "",
    cc_admins: true,
  });
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Email Delivery Logs State
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsStats, setLogsStats] = useState({ total: 0, sent: 0, failed: 0, skipped: 0 });
  const [logsStatusFilter, setLogsStatusFilter] = useState("all");
  const [logsSearch, setLogsSearch] = useState("");
  const [resendingId, setResendingId] = useState(null);
  const [resendingAll, setResendingAll] = useState(false);

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await API.mail.getLogs({
        status: logsStatusFilter !== "all" ? logsStatusFilter : undefined,
        search: logsSearch.trim() || undefined,
      });
      const payload = res?.stats ? res : (res?.data?.stats ? res.data : res?.data || res);
      const rows = Array.isArray(payload?.data)
        ? payload.data
        : (Array.isArray(payload?.logs?.data) ? payload.logs.data : Array.isArray(payload?.logs) ? payload.logs : []);
      setLogs(rows);
      if (payload?.stats) {
        setLogsStats({ total: 0, sent: 0, failed: 0, skipped: 0, ...payload.stats });
      } else {
        setLogsStats({ total: 0, sent: 0, failed: 0, skipped: 0 });
      }
    } catch (err) {
      console.error("Failed to load mail logs:", err);
      setLogs([]);
      setLogsStats({ total: 0, sent: 0, failed: 0, skipped: 0 });
      setMessage({ type: "error", text: err?.message || "Unable to load delivery logs. Ensure the email log migration has been run." });
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    API.fetchAdminEvents()
      .then((data) => {
        if (!active) return;
        setEvents(Array.isArray(data) ? data : (data?.data || []));
      })
      .catch(() => { if (active) setEvents([]); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (activeTab === "logs") {
      fetchLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, logsStatusFilter]);

  const handleResendSingle = async (id) => {
    try {
      setResendingId(id);
      const res = await API.mail.resend(id);
      const data = res?.data || res;
      if (data?.success) {
        successAlert("Email Resent", data.message || "Email was resent successfully.");
      } else {
        errorAlert("Resend Failed", data?.message || "Failed to resend email.");
      }
      fetchLogs();
    } catch (err) {
      errorAlert("Resend Error", err?.response?.data?.message || err?.message || "Error resending email.");
    } finally {
      setResendingId(null);
    }
  };

  const handleResendAllFailed = async () => {
    const result = await Swal.fire({
      title: "Resend All Failed Emails?",
      text: "This will attempt to resend all emails that encountered delivery failure.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#0f3373",
      confirmButtonText: "Yes, Resend All",
    });
    if (!result.isConfirmed) return;

    try {
      setResendingAll(true);
      const res = await API.mail.resendFailed();
      const data = res?.data || res;
      Swal.fire({
        icon: "success",
        title: "Resend Process Completed",
        html: `<p>${data?.message || "Processed failed emails."}</p><p class="mt-2 text-xs"><b>Resent successfully:</b> ${data?.resent_count ?? 0}<br><b>Failed again:</b> ${data?.failed_count ?? 0}</p>`,
      });
      fetchLogs();
    } catch (err) {
      errorAlert("Batch Resend Failed", err?.response?.data?.message || err?.message || "Could not process batch resend.");
    } finally {
      setResendingAll(false);
    }
  };

  const filteredRecipients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return recipients;
    return recipients.filter((row) => {
      const haystack = [
        row?.name,
        row?.email,
        row?.player_name,
        row?.event_title,
      ]
        .map((item) => String(item || "").toLowerCase())
        .join(" ");
      return haystack.includes(q);
    });
  }, [recipients, search]);

  const availableFromAddresses = useMemo(() => {
    const base = [];
    const primary = String(settings?.from_address || "").trim().toLowerCase();
    if (primary) base.push(primary);

    const extra = String(settings?.available_from_addresses || "")
      .split(/[,\n;]+/)
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);

    return Array.from(new Set([...base, ...extra]));
  }, [settings?.from_address, settings?.available_from_addresses]);

  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const allFilteredSelected =
    filteredRecipients.length > 0 &&
    filteredRecipients.every((row) => selectedSet.has(row.key));

  useEffect(() => {
    if (!message.text) return;
    if (message.type === "success") {
      successAlert("Success", message.text);
      return;
    }
    if (message.type === "error") {
      errorAlert("Error", message.text);
    }
  }, [message]);

  useEffect(() => {
    let active = true;
    const loadSettings = async () => {
      setSettingsLoading(true);
      try {
        const data = await API.fetchAdminMailSettings();
        if (!active) return;
        setSettings({
          ...defaultSettings,
          ...data,
          available_from_addresses: Array.isArray(data?.available_from_addresses)
            ? data.available_from_addresses.join(", ")
            : "",
          admin_notification_emails: Array.isArray(data?.admin_notification_emails)
            ? data.admin_notification_emails.join(", ")
            : "",
        });
      } catch (err) {
        if (!active) return;
        setMessage({
          type: "error",
          text: err?.message || "Unable to load mail settings.",
        });
      } finally {
        if (active) setSettingsLoading(false);
      }
    };

    loadSettings();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const loadTemplates = async () => {
      setTemplatesLoading(true);
      try {
        const data = await API.fetchAdminMailTemplates();
        if (!active) return;
        const rows = Array.isArray(data) ? data : [];
        setTemplates(rows);
        if (!rows.length) {
          setSelectedTemplateKey("");
          setTemplateForm(emptyTemplateState);
          return;
        }

        const selected = rows[0];
        setSelectedTemplateKey(selected.key || "");
        setTemplateForm({
          key: selected.key || "",
          name: selected.name || "",
          subject: selected.subject || "",
          body: selected.body || "",
          is_enabled: !!selected.is_enabled,
          available_variables: Array.isArray(selected.available_variables)
            ? selected.available_variables
            : [],
        });
      } catch (err) {
        if (!active) return;
        setMessage({
          type: "error",
          text: err?.message || "Unable to load workflow mail templates.",
        });
      } finally {
        if (active) setTemplatesLoading(false);
      }
    };

    loadTemplates();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const selected = templates.find((row) => row.key === selectedTemplateKey);
    if (!selected) return;
    setTemplateForm({
      key: selected.key || "",
      name: selected.name || "",
      subject: selected.subject || "",
      body: selected.body || "",
      is_enabled: !!selected.is_enabled,
      available_variables: Array.isArray(selected.available_variables)
        ? selected.available_variables
        : [],
    });
  }, [selectedTemplateKey, templates]);

  useEffect(() => {
    const primary = String(settings?.from_address || "").trim().toLowerCase();
    const fallback = availableFromAddresses[0] || "";
    const target = primary || fallback;
    if (!target) return;

    setCompose((prev) => {
      const current = String(prev.from_address || "").trim().toLowerCase();
      if (current && availableFromAddresses.includes(current)) return prev;
      return { ...prev, from_address: target };
    });
  }, [settings?.from_address, availableFromAddresses]);

  useEffect(() => {
    let active = true;
    const loadRecipients = async () => {
      setRecipientLoading(true);
      setSelectedKeys([]);
      try {
        const params = {};
        if (audience === "excellence_awards") {
          params.status = awardStatusFilter;
          params.year = awardYearFilter;
        }
        if (audience === "players") {
          params.status = playerProfileStatus;
          if (playerEventId) params.event_id = playerEventId;
        }
        const data = await API.fetchAdminMailRecipients(audience, params);
        if (!active) return;
        setRecipients(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!active) return;
        setRecipients([]);
        setMessage({
          type: "error",
          text: err?.message || "Unable to load recipients.",
        });
      } finally {
        if (active) setRecipientLoading(false);
      }
    };
    loadRecipients();
    return () => {
      active = false;
    };
  }, [audience, awardStatusFilter, awardYearFilter, playerProfileStatus, playerEventId]);

  if (!["admin", "secretary"].includes(normalizedRole)) {
    return (
      <div className="flex justify-center items-center h-screen text-red-500">
        Unauthorized Access
      </div>
    );
  }

  const updateSettingsField = (name, value) => {
    setSettings((prev) => ({ ...prev, [name]: value }));
  };

  const updateTemplateField = (name, value) => {
    setTemplateForm((prev) => ({ ...prev, [name]: value }));
  };

  const selectTemplate = (key) => {
    setSelectedTemplateKey(key);
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSettingsSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const payload = {
        is_enabled: !!settings.is_enabled,
        mailer: settings.mailer || "smtp",
        smtp_host: settings.smtp_host || "",
        smtp_port: settings.smtp_port ? Number(settings.smtp_port) : null,
        smtp_username: settings.smtp_username || "",
        smtp_password: settings.smtp_password || "",
        smtp_encryption: settings.smtp_encryption || "",
        from_address: settings.from_address || "",
        from_name: settings.from_name || "",
        available_from_addresses: String(settings.available_from_addresses || "")
          .split(/[,\n;]+/)
          .map((item) => item.trim())
          .filter(Boolean),
        reply_to_address: settings.reply_to_address || "",
        reply_to_name: settings.reply_to_name || "",
        admin_notification_emails: String(settings.admin_notification_emails || "")
          .split(/[,\n;]+/)
          .map((item) => item.trim())
          .filter(Boolean),
      };

      const res = await API.updateAdminMailSettings(payload);
      const data = res?.data || res || {};
      setSettings((prev) => ({
        ...prev,
        ...data,
        smtp_password: "",
        has_smtp_password: !!data?.has_smtp_password,
        admin_notification_emails: Array.isArray(data?.admin_notification_emails)
          ? data.admin_notification_emails.join(", ")
          : prev.admin_notification_emails,
        available_from_addresses: Array.isArray(data?.available_from_addresses)
          ? data.available_from_addresses.join(", ")
          : prev.available_from_addresses,
      }));
      setMessage({ type: "success", text: "Mail settings saved." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.message || "Unable to save mail settings.",
      });
    } finally {
      setSettingsSaving(false);
    }
  };

  const saveTemplate = async (e) => {
    e.preventDefault();
    if (!templateForm.key) return;

    setTemplateSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await API.updateAdminMailTemplate(templateForm.key, {
        subject: templateForm.subject,
        body: templateForm.body,
        is_enabled: !!templateForm.is_enabled,
      });
      const updated = res?.data || res || {};
      setTemplates((prev) =>
        prev.map((row) => (row.key === updated.key ? updated : row))
      );
      setTemplateForm((prev) => ({
        ...prev,
        subject: updated.subject || "",
        body: updated.body || "",
        is_enabled: !!updated.is_enabled,
        available_variables: Array.isArray(updated.available_variables)
          ? updated.available_variables
          : [],
      }));
      setMessage({ type: "success", text: "Mail template saved." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.message || "Unable to save mail template.",
      });
    } finally {
      setTemplateSaving(false);
    }
  };

  const resetTemplate = async () => {
    if (!templateForm.key) return;
    if (!window.confirm(`Reset "${templateForm.name}" to its default content?`)) {
      return;
    }

    setTemplateResetting(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await API.resetAdminMailTemplate(templateForm.key);
      const updated = res?.data || res || {};
      setTemplates((prev) =>
        prev.map((row) => (row.key === updated.key ? updated : row))
      );
      setTemplateForm({
        key: updated.key || "",
        name: updated.name || "",
        subject: updated.subject || "",
        body: updated.body || "",
        is_enabled: !!updated.is_enabled,
        available_variables: Array.isArray(updated.available_variables)
          ? updated.available_variables
          : [],
      });
      setMessage({ type: "success", text: "Mail template reset to default." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.message || "Unable to reset mail template.",
      });
    } finally {
      setTemplateResetting(false);
    }
  };

  const toggleRecipient = (key) => {
    setSelectedKeys((prev) => {
      if (prev.includes(key)) return prev.filter((item) => item !== key);
      return [...prev, key];
    });
  };

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      setSelectedKeys((prev) =>
        prev.filter((key) => !filteredRecipients.some((row) => row.key === key))
      );
      return;
    }
    const next = new Set(selectedKeys);
    filteredRecipients.forEach((row) => next.add(row.key));
    setSelectedKeys(Array.from(next));
  };

  const sendMail = async (e) => {
    e.preventDefault();
    setSending(true);
    setMessage({ type: "", text: "" });
    try {
      const payload = {
        audience,
        send_mode: compose.send_mode,
        recipient_keys: compose.send_mode === "selected" ? selectedKeys : [],
        from_address: compose.from_address || "",
        from_name: compose.from_name || "",
        additional_emails: compose.additional_emails,
        subject: compose.subject,
        body: compose.body,
        cc_admins: !!compose.cc_admins,
      };

      if (audience === "excellence_awards") {
        payload.status = awardStatusFilter;
        payload.year = awardYearFilter;
      }
      if (audience === "players") {
        payload.status = playerProfileStatus;
        if (playerEventId) payload.event_id = playerEventId;
      }

      const res = await API.sendAdminMail(payload);
      const data = res?.data || {};
      const firstError =
        Array.isArray(data?.errors) && data.errors.length > 0
          ? String(data.errors[0]?.error || "").trim()
          : "";
      const effectiveFrom = String(
        data?.effective_from_address || payload.from_address || ""
      ).trim();
      setMessage({
        type: "success",
        text: `Mail processed. Sent: ${data?.sent || 0}, Failed: ${data?.failed || 0}${effectiveFrom ? `, From: ${effectiveFrom}` : ""}${firstError ? `. First error: ${firstError}` : ""}`,
      });
      setCompose((prev) => ({
        ...prev,
        subject: "",
        body: "",
        additional_emails: "",
      }));
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.message || "Unable to send mail.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-6" style={{ marginTop: "20px" }}>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-indigo-900 bg-clip-text text-transparent">
                ✉️ Mail Center
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Configure SMTP servers, custom template workflows, and broadcast notifications.
              </p>
            </div>
          </div>

          {/* Premium Tab Bar */}
          <div className="flex flex-wrap border-b border-slate-200 gap-1 bg-white p-2 rounded-2xl border">
            <button
              onClick={() => setActiveTab("send")}
              className={`py-2 px-4 sm:px-6 text-xs sm:text-sm font-bold rounded-xl transition-all ${
                activeTab === "send"
                  ? "bg-slate-900 text-white shadow"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              ✉️ Send Custom Mail
            </button>
            <button
              onClick={() => setActiveTab("templates")}
              className={`py-2 px-4 sm:px-6 text-xs sm:text-sm font-bold rounded-xl transition-all ${
                activeTab === "templates"
                  ? "bg-slate-900 text-white shadow"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              📋 Workflow Templates
            </button>
            <button
              onClick={() => setActiveTab("smtp")}
              className={`py-2 px-4 sm:px-6 text-xs sm:text-sm font-bold rounded-xl transition-all ${
                activeTab === "smtp"
                  ? "bg-slate-900 text-white shadow"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              ⚙️ SMTP Settings
            </button>
            <button
              onClick={() => setActiveTab("logs")}
              className={`py-2 px-4 sm:px-6 text-xs sm:text-sm font-bold rounded-xl transition-all ${
                activeTab === "logs"
                  ? "bg-slate-900 text-white shadow"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              📊 Delivery Logs & Reports
            </button>
          </div>

          {/* Feedback message bar */}
          {message.text && (
            <div
              className={`p-4 rounded-xl border text-sm flex items-center justify-between ${
                message.type === "success"
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-red-50 border-red-200 text-red-800"
              }`}
            >
              <span>{message.text}</span>
              <button
                onClick={() => setMessage({ type: "", text: "" })}
                className="text-xs font-bold underline"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Tab 1: SMTP Settings */}
          {activeTab === "smtp" && (
            <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-md space-y-6 animate-fadeIn">
              <div>
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  ⚙️ SMTP Mail Settings
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Configure mail server connection details. Ensure SSL/TLS port and encryption parameters are validated.
                </p>
              </div>

              {settingsLoading ? (
                <div className="py-12 text-center text-slate-400">Loading settings...</div>
              ) : (
                <form onSubmit={saveSettings} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center gap-2.5 md:col-span-2 p-3 bg-slate-50 rounded-xl border border-slate-150 text-sm font-semibold text-slate-700">
                    <input
                      type="checkbox"
                      checked={!!settings.is_enabled}
                      onChange={(e) => updateSettingsField("is_enabled", e.target.checked)}
                      className="h-4 w-4 text-indigo-650 rounded border-slate-350"
                    />
                    Enable outgoing emails
                  </label>

                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">Mailer</label>
                    <input
                      type="text"
                      value={settings.mailer || ""}
                      onChange={(e) => updateSettingsField("mailer", e.target.value)}
                      placeholder="smtp"
                      className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">SMTP Host</label>
                    <input
                      type="text"
                      value={settings.smtp_host || ""}
                      onChange={(e) => updateSettingsField("smtp_host", e.target.value)}
                      placeholder="smtp.mailtrap.io"
                      className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">SMTP Port</label>
                    <input
                      type="number"
                      value={settings.smtp_port || ""}
                      onChange={(e) => updateSettingsField("smtp_port", e.target.value)}
                      placeholder="587"
                      className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">Encryption</label>
                    <input
                      type="text"
                      value={settings.smtp_encryption || ""}
                      onChange={(e) => updateSettingsField("smtp_encryption", e.target.value)}
                      placeholder="tls"
                      className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">SMTP Username</label>
                    <input
                      type="text"
                      value={settings.smtp_username || ""}
                      onChange={(e) => updateSettingsField("smtp_username", e.target.value)}
                      placeholder="SMTP Username"
                      className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">SMTP Password</label>
                    <input
                      type="password"
                      value={settings.smtp_password || ""}
                      onChange={(e) => updateSettingsField("smtp_password", e.target.value)}
                      placeholder={
                        settings.has_smtp_password
                          ? "Leave blank to keep existing password"
                          : "SMTP Password"
                      }
                      className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">Default From Email</label>
                    <input
                      type="email"
                      value={settings.from_address || ""}
                      onChange={(e) => updateSettingsField("from_address", e.target.value)}
                      placeholder="info@parasports.org"
                      className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">Default From Name</label>
                    <input
                      type="text"
                      value={settings.from_name || ""}
                      onChange={(e) => updateSettingsField("from_name", e.target.value)}
                      placeholder="Para Sports Association"
                      className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block mb-1 text-xs font-semibold text-slate-600">Allowed From Addresses (comma/newline separated)</label>
                    <textarea
                      value={settings.available_from_addresses || ""}
                      onChange={(e) => updateSettingsField("available_from_addresses", e.target.value)}
                      placeholder="Use comma or newline. Example: team1@mail.com, team2@mail.com"
                      className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none min-h-[90px] resize-y"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">Reply-To Email</label>
                    <input
                      type="email"
                      value={settings.reply_to_address || ""}
                      onChange={(e) => updateSettingsField("reply_to_address", e.target.value)}
                      placeholder="support@parasports.org"
                      className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">Reply-To Name</label>
                    <input
                      type="text"
                      value={settings.reply_to_name || ""}
                      onChange={(e) => updateSettingsField("reply_to_name", e.target.value)}
                      placeholder="Support Team"
                      className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block mb-1 text-xs font-semibold text-slate-600">Admin Notification Emails (comma/newline separated)</label>
                    <textarea
                      value={settings.admin_notification_emails || ""}
                      onChange={(e) => updateSettingsField("admin_notification_emails", e.target.value)}
                      placeholder="Example: admin@mail.com, secretary@mail.com"
                      className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none min-h-[90px] resize-y"
                    />
                  </div>

                  <div className="md:col-span-2 pt-4">
                    <button
                      type="submit"
                      disabled={settingsSaving}
                      className="px-6 py-3 rounded-xl bg-indigo-600 text-white font-bold text-sm hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                    >
                      {settingsSaving ? "Saving Settings..." : "💾 Save Mail Settings"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* Tab 2: Workflow Templates */}
          {activeTab === "templates" && (
            <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-md space-y-6 animate-fadeIn">
              <div>
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  📋 Workflow Templates
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Customize templates for event registrations, player onboarding OTPs, ID card links and merit certificates.
                </p>
              </div>

              {templatesLoading ? (
                <div className="py-12 text-center text-slate-400">Loading templates...</div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-6">
                  <div className="border border-slate-200 rounded-2xl p-3 space-y-2 max-h-[500px] overflow-y-auto bg-slate-50/50">
                    {templates.map((template) => {
                      const active = template.key === selectedTemplateKey;
                      return (
                        <button
                          key={template.key}
                          type="button"
                          onClick={() => selectTemplate(template.key)}
                          className={`w-full text-left border rounded-xl px-3 py-3 transition-all ${
                            active
                              ? "border-indigo-500 bg-white shadow-md ring-2 ring-indigo-500/20"
                              : "border-slate-200 hover:bg-white"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-bold text-xs text-slate-800 truncate">{template.name}</p>
                            <span
                              className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                                template.is_enabled
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-slate-200 text-slate-600"
                              }`}
                            >
                              {template.is_enabled ? "Active" : "Off"}
                            </span>
                          </div>
                          <p className="text-[10px] text-slate-400 mt-1 font-mono">{template.key}</p>
                        </button>
                      );
                    })}
                    {!templates.length && (
                      <p className="text-sm text-slate-500 text-center py-6">No templates found.</p>
                    )}
                  </div>

                  <div className="border border-slate-200 rounded-2xl p-5 bg-white">
                    {!templateForm.key ? (
                      <div className="text-center py-20 text-slate-400 text-sm">
                        Select a workflow template from the list to preview and edit its content.
                      </div>
                    ) : (
                      <form onSubmit={saveTemplate} className="space-y-4">
                        <div className="flex flex-col gap-1 border-b pb-3 mb-3">
                          <h3 className="text-md font-bold text-slate-800">{templateForm.name}</h3>
                          <p className="text-xs font-mono text-slate-400">{templateForm.key}</p>
                        </div>

                        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                          <input
                            type="checkbox"
                            checked={!!templateForm.is_enabled}
                            onChange={(e) => updateTemplateField("is_enabled", e.target.checked)}
                            className="h-4 w-4 text-indigo-650 rounded border-slate-350"
                          />
                          Enable this automatic email
                        </label>

                        <div>
                          <label className="block mb-1 text-xs font-semibold text-slate-600">Email Subject</label>
                          <input
                            type="text"
                            value={templateForm.subject}
                            onChange={(e) => updateTemplateField("subject", e.target.value)}
                            className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                            required
                          />
                        </div>

                        <div>
                          <label className="block mb-1 text-xs font-semibold text-slate-600">Email Body Markup</label>
                          <textarea
                            value={templateForm.body}
                            onChange={(e) => updateTemplateField("body", e.target.value)}
                            className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none min-h-[220px] font-mono resize-y"
                            required
                          />
                        </div>

                        <div>
                          <p className="text-xs font-bold text-slate-600 mb-2">Available Variables (click brackets to copy)</p>
                          <div className="flex flex-wrap gap-1.5">
                            {(templateForm.available_variables || []).map((item) => (
                              <code
                                key={item}
                                onClick={() => {
                                  navigator.clipboard.writeText(`{{${item}}}`);
                                  Swal.fire({
                                    title: "Copied!",
                                    text: `{{${item}}} copied to clipboard.`,
                                    icon: "success",
                                    timer: 1000,
                                    showConfirmButton: false
                                  });
                                }}
                                className="rounded bg-slate-100 hover:bg-slate-200 px-2.5 py-1 text-xs text-slate-700 font-mono font-bold cursor-pointer border border-slate-200"
                              >
                                {"{?"}{item}{"?}"}
                              </code>
                            ))}
                            {!templateForm.available_variables?.length && (
                              <p className="text-xs text-slate-400 italic">No custom variables available for this template.</p>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-3 pt-3 border-t">
                          <button
                            type="submit"
                            disabled={templateSaving}
                            className="px-5 py-2.5 rounded-xl bg-indigo-600 text-white font-bold text-xs hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                          >
                            {templateSaving ? "Saving..." : "Save Template"}
                          </button>
                          <button
                            type="button"
                            onClick={resetTemplate}
                            disabled={templateResetting}
                            className="px-5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-700 font-bold text-xs hover:bg-slate-50 disabled:opacity-50 transition-colors"
                          >
                            {templateResetting ? "Resetting..." : "Reset to Default"}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab 3: Send Custom Mail */}
          {activeTab === "send" && (
            <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-md space-y-6 animate-fadeIn">
              <div>
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                  ✉️ Send Custom Broadcast Mail
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Send custom newsletters, alerts, notifications, or manual invitations to players, escorts, or volunteers.
                </p>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap gap-2 p-1.5 bg-slate-100 rounded-2xl w-fit border">
                  {AUDIENCES.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => {
                        setAudience(item.key);
                        setSelectedKeys([]);
                      }}
                      className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all ${
                        audience === item.key
                          ? "bg-slate-900 text-white shadow"
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>

                {audience === "players" && (
                  <div className="flex flex-wrap gap-4 p-4 bg-slate-50 border rounded-2xl">
                    <div>
                      <label className="block mb-1 text-[10px] uppercase font-bold text-slate-500">Event</label>
                      <select
                        className="bg-white border rounded-xl px-3 py-1.5 text-xs text-slate-700 focus:outline-none min-w-[220px]"
                        value={playerEventId}
                        onChange={(e) => setPlayerEventId(e.target.value)}
                      >
                        <option value="">All player profiles</option>
                        {events.map((event) => <option key={event.id} value={event.id}>{event.title || `Event #${event.id}`}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block mb-1 text-[10px] uppercase font-bold text-slate-500">Profile Status</label>
                      <select
                        className="bg-white border rounded-xl px-3 py-1.5 text-xs text-slate-700 focus:outline-none"
                        value={playerProfileStatus}
                        onChange={(e) => setPlayerProfileStatus(e.target.value)}
                      >
                        <option value="all">All</option>
                        <option value="profile_pending">Profile Pending</option>
                        <option value="approved">Approved</option>
                      </select>
                    </div>
                    <p className="self-end text-xs text-slate-500">These filters control both the recipient list and “send to all” delivery.</p>
                  </div>
                )}

                {audience === "excellence_awards" && (
                  <div className="flex flex-wrap gap-4 p-4 bg-slate-50 border rounded-2xl max-w-fit">
                    <div>
                      <label className="block mb-1 text-[10px] uppercase font-bold text-slate-500 text-left">Filter Status</label>
                      <select
                        className="bg-white border rounded-xl px-3 py-1.5 text-xs text-slate-700 focus:outline-none"
                        value={awardStatusFilter}
                        onChange={(e) => setAwardStatusFilter(e.target.value)}
                      >
                        <option value="all">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                    <div>
                      <label className="block mb-1 text-[10px] uppercase font-bold text-slate-500 text-left">Filter Year</label>
                      <input
                        type="number"
                        placeholder="E.g. 2026"
                        className="bg-white border rounded-xl px-3 py-1.5 text-xs text-slate-700 focus:outline-none w-28"
                        value={awardYearFilter}
                        onChange={(e) => setAwardYearFilter(e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[360px_minmax(0,1fr)] gap-6">
                <div className="border border-slate-200 rounded-2xl p-4 bg-slate-50/30 flex flex-col space-y-4 max-h-[580px]">
                  <div>
                    <label className="block mb-1.5 text-xs font-bold text-slate-600">Search Recipients</label>
                    <div className="relative">
                      <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search name, email, event..."
                        className="w-full text-sm border border-slate-300 rounded-xl px-3 py-2 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3 bg-white p-2 border rounded-xl">
                    <span className="text-xs text-slate-500 font-bold px-1">
                      Selected: {selectedKeys.length} / {filteredRecipients.length}
                    </span>
                    <button
                      type="button"
                      onClick={toggleSelectAllFiltered}
                      className="px-2.5 py-1 text-[11px] font-bold rounded-lg border border-slate-350 bg-slate-50 hover:bg-slate-100 text-slate-700"
                    >
                      {allFilteredSelected ? "Unselect All" : "Select All"}
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto space-y-2 max-h-[380px] pr-1">
                    {recipientLoading ? (
                      <div className="text-center py-12 text-slate-400 text-xs">Loading recipients...</div>
                    ) : (
                      filteredRecipients.map((row) => (
                        <label
                          key={row.key}
                          className={`flex items-start gap-2.5 border rounded-xl p-3 cursor-pointer transition-all bg-white hover:border-slate-350 ${
                            selectedSet.has(row.key)
                              ? "border-indigo-500 ring-2 ring-indigo-500/10 shadow-sm"
                              : "border-slate-200"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedSet.has(row.key)}
                            onChange={() => toggleRecipient(row.key)}
                            className="mt-0.5 h-4 w-4 text-indigo-650 rounded border-slate-350"
                          />
                          <div className="text-xs space-y-0.5 select-none">
                            <p className="font-extrabold text-slate-800 leading-none">{row.name || "No Name"}</p>
                            <p className="text-slate-650 font-mono font-medium">{row.email}</p>
                            {row.player_name && (
                              <p className="text-[10px] text-slate-500">Player: <span className="font-semibold">{row.player_name}</span></p>
                            )}
                            {row.event_title && (
                              <p className="text-[10px] text-slate-500">Event: <span className="font-semibold">{row.event_title}</span></p>
                            )}
                          </div>
                        </label>
                      ))
                    )}
                    {!filteredRecipients.length && !recipientLoading && (
                      <p className="text-xs text-slate-400 italic text-center py-12">No recipients found.</p>
                    )}
                  </div>
                </div>

                <form onSubmit={sendMail} className="border border-slate-200 rounded-2xl p-5 bg-white space-y-4">
                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">Send Mode</label>
                    <select
                      value={compose.send_mode}
                      onChange={(e) => setCompose((prev) => ({ ...prev, send_mode: e.target.value }))}
                      className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    >
                      <option value="selected">Send only to SELECTED recipients ({selectedKeys.length})</option>
                      <option value="all">Send to ALL {audience} in database ({recipients.length})</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-1 text-xs font-semibold text-slate-600">From Address</label>
                      <select
                        value={compose.from_address}
                        onChange={(e) =>
                          setCompose((prev) => ({ ...prev, from_address: e.target.value }))
                        }
                        required
                        className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      >
                        {!availableFromAddresses.length && (
                          <option value="">No allowed address configured</option>
                        )}
                        {availableFromAddresses.map((email) => (
                          <option key={email} value={email}>
                            {email}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block mb-1 text-xs font-semibold text-slate-600">From Name (optional override)</label>
                      <input
                        type="text"
                        value={compose.from_name}
                        onChange={(e) => setCompose((prev) => ({ ...prev, from_name: e.target.value }))}
                        className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        placeholder={settings.from_name || "Default Sender"}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">Additional CC Emails (comma/newline separated)</label>
                    <textarea
                      value={compose.additional_emails}
                      onChange={(e) =>
                        setCompose((prev) => ({ ...prev, additional_emails: e.target.value }))
                      }
                      className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none min-h-[70px] resize-y"
                      placeholder="Example: backup@mail.com, copy@mail.com"
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">Subject</label>
                    <input
                      type="text"
                      value={compose.subject}
                      onChange={(e) => setCompose((prev) => ({ ...prev, subject: e.target.value }))}
                      className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      required
                    />
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-semibold text-slate-600">Message Markdown Body</label>
                    <textarea
                      value={compose.body}
                      onChange={(e) => setCompose((prev) => ({ ...prev, body: e.target.value }))}
                      className="w-full text-sm border border-slate-300 rounded-xl px-4 py-2.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none min-h-[160px] resize-y font-sans"
                      required
                    />
                  </div>

                  <label className="flex items-center gap-2.5 text-sm font-semibold text-slate-700">
                    <input
                      type="checkbox"
                      checked={!!compose.cc_admins}
                      onChange={(e) => setCompose((prev) => ({ ...prev, cc_admins: e.target.checked }))}
                      className="h-4 w-4 text-indigo-650 rounded border-slate-350"
                    />
                    Include configured admin notification emails in CC
                  </label>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={sending}
                      className="px-6 py-3 rounded-xl bg-indigo-600 text-white font-bold text-sm hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                    >
                      {sending ? "Sending emails..." : "🚀 Send Out Broadcast"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Tab 4: Delivery Logs & Reports */}
          {activeTab === "logs" && (
            <div className="space-y-6 animate-fadeIn">
              {/* KPI Stat Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                    Total Emails Logged
                  </div>
                  <div className="text-3xl font-black text-slate-900">{logsStats.total}</div>
                  <p className="text-xs text-slate-400 mt-1">All recorded outbound system emails</p>
                </div>
                <div className="bg-white border border-emerald-200 rounded-2xl p-5 shadow-sm bg-gradient-to-br from-white to-emerald-50/50">
                  <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1">
                    Sent Successfully
                  </div>
                  <div className="text-3xl font-black text-emerald-700">{logsStats.sent}</div>
                  <p className="text-xs text-emerald-600 mt-1">Accepted by the configured SMTP server</p>
                </div>
                <div className="bg-white border border-rose-200 rounded-2xl p-5 shadow-sm bg-gradient-to-br from-white to-rose-50/50">
                  <div className="text-xs font-bold uppercase tracking-wider text-rose-700 mb-1">
                    Rejected / Failed Delivery
                  </div>
                  <div className="text-3xl font-black text-rose-700">{logsStats.failed}</div>
                  <p className="text-xs text-rose-600 mt-1">Bounced, rejected or connection timed out</p>
                </div>
                <div className="bg-white border border-amber-200 rounded-2xl p-5 shadow-sm">
                  <div className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-1">Skipped</div>
                  <div className="text-3xl font-black text-amber-700">{logsStats.skipped || 0}</div>
                  <p className="text-xs text-amber-600 mt-1">Not sent because mail was disabled</p>
                </div>
              </div>

              {/* Toolbar & Filters */}
              <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex flex-col md:flex-row items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                  <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Status:</span>
                  {["all", "sent", "failed"].map((st) => (
                    <button
                      key={st}
                      onClick={() => setLogsStatusFilter(st)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition capitalize ${
                        logsStatusFilter === st
                          ? "bg-slate-900 text-white shadow-sm"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {st === "all" ? "All Logs" : st === "failed" ? "Failed / Rejected" : "Sent"}
                    </button>
                  ))}
                  <input
                    type="text"
                    placeholder="Search recipient or subject..."
                    value={logsSearch}
                    onChange={(e) => setLogsSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && fetchLogs()}
                    className="border border-slate-300 rounded-xl px-3 py-1.5 text-xs bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 w-48 sm:w-64"
                  />
                  <button
                    onClick={fetchLogs}
                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold border border-slate-200"
                  >
                    Refresh
                  </button>
                </div>

                {logsStats.failed > 0 && (
                  <button
                    onClick={handleResendAllFailed}
                    disabled={resendingAll}
                    className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded-xl text-xs font-bold shadow-sm transition flex items-center gap-1.5 whitespace-nowrap self-end md:self-auto"
                  >
                    {resendingAll ? "Resending..." : `🔄 Resend All Failed (${logsStats.failed})`}
                  </button>
                )}
              </div>

              {/* Logs Table */}
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs text-slate-800">
                    <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600 font-semibold border-b border-slate-200">
                      <tr>
                        <th className="p-3.5 pl-5">Recipient</th>
                        <th className="p-3.5">Subject</th>
                        <th className="p-3.5">Content</th>
                        <th className="p-3.5">Attachments</th>
                        <th className="p-3.5 text-center">Status</th>
                        <th className="p-3.5">Rejection / Error Details</th>
                        <th className="p-3.5 text-center">Attempts</th>
                        <th className="p-3.5">Timestamp</th>
                        <th className="p-3.5 pr-5 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {logsLoading ? (
                        <tr>
                          <td colSpan={9} className="p-8 text-center text-slate-400">
                            Loading delivery logs...
                          </td>
                        </tr>
                      ) : logs.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="p-8 text-center text-slate-400">
                            No delivery logs found matching the selected filter.
                          </td>
                        </tr>
                      ) : (
                        logs.map((log) => (
                          <tr key={log.id} className="hover:bg-slate-50/80 transition">
                            <td className="p-3.5 pl-5">
                              <div className="font-bold text-slate-900">{log.recipient}</div>
                              {log.audience && (
                                <span className="inline-block mt-0.5 px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-semibold rounded-md border border-slate-200">
                                  {log.audience}
                                </span>
                              )}
                            </td>
                            <td className="p-3.5 font-medium text-slate-800 max-w-xs truncate" title={log.subject}>
                              {log.subject}
                            </td>
                            <td className="p-3.5 max-w-sm">
                              <details className="text-[11px]">
                                <summary className="cursor-pointer font-bold text-indigo-700">View message</summary>
                                <div className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-2 text-slate-700">
                                  {String(log.body || "No content recorded").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()}
                                </div>
                              </details>
                            </td>
                            <td className="p-3.5 max-w-xs">
                              {Array.isArray(log.attachments) && log.attachments.length > 0 ? (
                                <div className="space-y-1">
                                  {log.attachments.map((attachment, index) => (
                                    <div key={`${log.id}-attachment-${index}`} className="rounded-lg border border-indigo-100 bg-indigo-50 px-2 py-1 text-[10px] font-semibold text-indigo-800 break-all">
                                      {attachment.name || "Attachment"}
                                      {attachment.mime ? <span className="block font-normal text-indigo-500">{attachment.mime}</span> : null}
                                    </div>
                                  ))}
                                </div>
                              ) : <span className="text-slate-400 italic">None</span>}
                            </td>
                            <td className="p-3.5 text-center">
                              <span
                                className={`px-2.5 py-1 rounded-full text-[10px] font-bold inline-flex items-center gap-1 ${
                                  log.status === "sent"
                                    ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                                    : log.status === "skipped"
                                      ? "bg-amber-100 text-amber-800 border border-amber-300"
                                    : "bg-rose-100 text-rose-800 border border-rose-300"
                                }`}
                              >
                                {log.status === "sent" ? "DELIVERED" : log.status === "skipped" ? "SKIPPED" : "FAILED"}
                              </span>
                            </td>
                            <td className="p-3.5 max-w-sm">
                              {["failed", "rejected", "skipped"].includes(log.status) ? (
                                <div className="text-rose-700 bg-rose-50/70 border border-rose-200 p-2 rounded-lg text-[11px] font-mono leading-tight break-words">
                                  {log.error_message || "Delivery rejected by mail server"}
                                </div>
                              ) : (
                                <span className="text-slate-400 text-[11px] italic">Delivered cleanly via SMTP</span>
                              )}
                            </td>
                            <td className="p-3.5 text-center font-bold text-slate-700">
                              {log.attempts || 1}
                            </td>
                            <td className="p-3.5 text-slate-500 font-mono text-[11px] whitespace-nowrap">
                              {log.created_at ? new Date(log.created_at).toLocaleString() : "—"}
                            </td>
                            <td className="p-3.5 pr-5 text-right">
                              <button
                                onClick={() => handleResendSingle(log.id)}
                                disabled={resendingId === log.id}
                                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg text-xs font-bold border border-slate-300 shadow-sm transition disabled:opacity-50"
                              >
                                {resendingId === log.id ? "Sending..." : "Resend"}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

        </div>
      </main>
      <Footer />
    </>
  );
};

export default MailCenter;
